import {createContext, use, type PropsWithChildren} from 'react'
import type {ContributedInstructionKind} from '@github-ui/ahp-session-client/contracts'

export interface ContributedInstruction {
  readonly kind: ContributedInstructionKind
  readonly label: string
  readonly prompt: string
  readonly ownerUrl?: string
}

export type ContributedInstructionSnapshot = readonly ContributedInstruction[]

const EMPTY_INSTRUCTIONS: ContributedInstructionSnapshot = []
const ContributedInstructionsContext = createContext<ContributedInstructionSnapshot | null>(null)
const NO_PERSONAL_INSTRUCTIONS_PROVIDER = Symbol('no-personal-instructions-provider')
const PersonalInstructionsContext = createContext<string | null | typeof NO_PERSONAL_INSTRUCTIONS_PROVIDER>(
  NO_PERSONAL_INSTRUCTIONS_PROVIDER,
)
const UpdatePersonalInstructionsContext = createContext<((personalInstructions: string) => void) | null>(null)

export function ContributedInstructionsProvider({
  instructions,
  children,
}: PropsWithChildren<{instructions: ContributedInstructionSnapshot}>) {
  return <ContributedInstructionsContext value={instructions}>{children}</ContributedInstructionsContext>
}

export function useHasContributedInstructionsProvider(): boolean {
  return use(ContributedInstructionsContext) !== null
}

export function useContributedInstructionSnapshot(): ContributedInstructionSnapshot {
  const snapshot = use(ContributedInstructionsContext)
  if (!snapshot) {
    throw new Error('useContributedInstructionSnapshot must be used within a ContributedInstructionsProvider')
  }
  return snapshot
}

export function useOptionalContributedInstructionSnapshot(): ContributedInstructionSnapshot {
  return use(ContributedInstructionsContext) ?? EMPTY_INSTRUCTIONS
}

export function PersonalInstructionsEditorProvider({
  personalInstructions,
  onPersonalInstructionsChange,
  children,
}: PropsWithChildren<{
  personalInstructions: string | null
  onPersonalInstructionsChange: (personalInstructions: string) => void
}>) {
  return (
    <UpdatePersonalInstructionsContext value={onPersonalInstructionsChange}>
      <PersonalInstructionsContext value={personalInstructions}>{children}</PersonalInstructionsContext>
    </UpdatePersonalInstructionsContext>
  )
}

export function useOptionalPersonalInstructionsEditor():
  | {
      personalInstructions: string | null
      updatePersonalInstructions: (personalInstructions: string) => void
    }
  | undefined {
  const personalInstructions = use(PersonalInstructionsContext)
  const updatePersonalInstructions = use(UpdatePersonalInstructionsContext)
  if (personalInstructions === NO_PERSONAL_INSTRUCTIONS_PROVIDER || !updatePersonalInstructions) return undefined
  return {personalInstructions, updatePersonalInstructions}
}
